// Date-string helpers for the text date columns (date de naissance, date de
// recrutement). The API stores "YYYY-MM-DD"; older rows may still hold
// "DD/MM/YYYY". Every screen reads through these helpers so both forms display
// and edit correctly, and every form sends the canonical ISO form.

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/;
const DMY = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/;

function build(year: number, month: number, day: number): string | null {
  if (year < 1900 || year > 2100 || month < 1 || month > 12 || day < 1 || day > 31) return null;
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (probe.getUTCFullYear() !== year || probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** "YYYY-MM-DD" for any readable date text (ISO, ISO datetime, DD/MM/YYYY), else ''. */
export function toIsoDate(value: string | null | undefined): string {
  if (!value) return '';
  const text = value.trim();
  let m = ISO_DATE.exec(text);
  if (m) return build(Number(m[1]), Number(m[2]), Number(m[3])) ?? '';
  m = DMY.exec(text);
  if (m) return build(Number(m[3]), Number(m[2]), Number(m[1])) ?? '';
  return '';
}

/** "DD/MM/YYYY" for any readable date text, else '' (never "Invalid Date"). */
export function toDisplayDate(value: string | null | undefined): string {
  const iso = toIsoDate(value);
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

/** Local Date at midnight for any readable date text, else null. */
export function parseDateString(value: string | null | undefined): Date | null {
  const iso = toIsoDate(value);
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** French long form, e.g. "29 mars 1989"; falls back to the raw text when unreadable. */
export function formatDateFrLong(value: string | null | undefined, fallback = '-'): string {
  const date = parseDateString(value);
  if (!date) return value && value.trim() ? value : fallback;
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
}
